// Banking-context mock data for OptiKurve platform
// All values deterministic for consistent UI demo

export const EVENT_TYPES = [
  "credit_card_request",
  "credit_card_request_abandoned",
  "credit_card_approved",
  "credit_card_activation",
  "credit_card_usage",
  "loan_application_request",
  "loan_application_abandoned",
  "loan_approved",
  "account_opening_request",
  "account_opened",
  "large_transfer_request",
  "kyc_document_upload",
  "kyc_failed",
  "mobile_app_login",
  "password_reset_request",
  "beneficiary_added",
  "reminder_sent",
  "reminder_read",
];

export const EVENT_SOURCES = [
  "web",
  "mobile_app",
  "branch_pos",
  "atm_kiosk",
  "core_banking_erp",
  "call_center",
  "partner_api",
];

const CUSTOMERS = [
  "CUST-10428", "CUST-10429", "CUST-10430", "CUST-10431", "CUST-10432",
  "CUST-10433", "CUST-10434", "CUST-10435", "CUST-10436", "CUST-10437",
  "CUST-10438", "CUST-10439", "CUST-10440", "CUST-10441", "CUST-10442",
];

const CARD_PRODUCTS = ["Platinum Rewards", "World Elite", "Cashback Gold", "Business Corporate", "Student Starter"];
const CHANNELS = ["Online Banking", "Mobile iOS", "Mobile Android", "Branch #204 Riyadh", "Branch #118 Jeddah", "ATM Terminal T-8842"];

function pad(n, w = 2) { return String(n).padStart(w, "0"); }

// Deterministic pseudo-random using seed
function seededRand(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function pick(arr, seed) {
  return arr[Math.floor(seededRand(seed) * arr.length)];
}

function makePayload(type, seed) {
  const amount = (seededRand(seed) * 15000 + 50).toFixed(2);
  const country = pick(["US", "CA", "GB", "FR", "AU"], seed + 3);
  switch (type) {
    case "credit_card_request":
    case "credit_card_request_abandoned":
    case "credit_card_approved":
    case "credit_card_activation":
      return {
        product: pick(CARD_PRODUCTS, seed),
        requested_limit: Math.round(seededRand(seed + 1) * 40000 + 5000),
        score_fico: Math.round(seededRand(seed + 2) * 300 + 550),
        channel: pick(CHANNELS, seed + 5),
        country,
        step_abandoned: type === "credit_card_request_abandoned" ? pick(["address_verification", "income_declaration", "id_upload", "consent"], seed + 6) : null,
      };
    case "credit_card_usage":
      return {
        merchant: pick(["Amazon", "Walmart", "Uber Eats", "Costco", "Netflix", "Apple", "Starbucks", "Tim Hortons", "Target", "Whole Foods"], seed),
        mcc: pick(["5411", "5814", "5732", "4121", "4899"], seed + 1),
        amount_local: amount,
        currency: pick(["USD", "CAD", "EUR", "GBP"], seed + 2),
        country,
        card_present: seededRand(seed + 4) > 0.4,
      };
    case "large_transfer_request":
      return {
        amount: Math.round(seededRand(seed) * 90000 + 10000),
        currency: "USD",
        beneficiary_country: country,
        purpose_code: pick(["SALARY", "INVEST", "FAMILY", "TRADE"], seed + 2),
      };
    case "kyc_document_upload":
    case "kyc_failed":
      return {
        document_type: pick(["national_id", "passport", "utility_bill", "salary_certificate"], seed),
        ocr_confidence: (seededRand(seed + 1) * 0.4 + 0.55).toFixed(3),
        reason: type === "kyc_failed" ? pick(["blurry_image", "expired_document", "mismatch_name"], seed + 2) : null,
      };
    case "loan_application_request":
    case "loan_application_abandoned":
    case "loan_approved":
      return {
        product: pick(["Personal Loan", "Auto Loan", "Home Mortgage"], seed),
        requested_amount: Math.round(seededRand(seed + 1) * 500000 + 20000),
        tenor_months: pick([12, 24, 36, 48, 60], seed + 2),
        dti_ratio: (seededRand(seed + 3) * 0.35 + 0.1).toFixed(2),
        step_abandoned: type === "loan_application_abandoned" ? pick(["income_proof", "employment_history", "collateral", "consent"], seed + 5) : null,
      };
    case "account_opening_request":
    case "account_opened":
      return {
        product: pick(["Current", "Savings", "Youth", "Business"], seed),
        branch: pick(CHANNELS, seed + 1),
        referred_by: pick(["organic", "campaign_Q1", "partner_referral"], seed + 2),
      };
    case "mobile_app_login":
      return { device: pick(["iPhone 15 Pro", "Galaxy S24", "Pixel 8", "iPhone 13"], seed), ip: `10.${Math.floor(seededRand(seed + 1) * 255)}.${Math.floor(seededRand(seed + 2) * 255)}.${Math.floor(seededRand(seed + 3) * 255)}`, geo: country };
    case "reminder_sent":
    case "reminder_read":
      return {
        campaign: pick(["Card Activation Reminder", "Abandoned Request Recovery", "Loan Follow-up", "Beneficiary Nudge"], seed),
        channel: pick(["push", "email", "sms"], seed + 1),
        opened: type === "reminder_read",
      };
    default:
      return { note: "generic event", channel: pick(CHANNELS, seed) };
  }
}

// Generate 240 events deterministically
export const EVENTS = (() => {
  const list = [];
  const now = new Date("2026-02-14T10:00:00Z");
  for (let i = 0; i < 240; i++) {
    const type = EVENT_TYPES[Math.floor(seededRand(i * 3.1) * EVENT_TYPES.length)];
    const source = EVENT_SOURCES[Math.floor(seededRand(i * 7.7) * EVENT_SOURCES.length)];
    const customer = CUSTOMERS[Math.floor(seededRand(i * 1.9) * CUSTOMERS.length)];
    const t = new Date(now.getTime() - i * (30 * 60 * 1000 + Math.floor(seededRand(i * 2.3) * 45 * 60 * 1000)));
    list.push({
      event_id: `evt_${pad(9200000 + i, 8)}`,
      customer_code: customer,
      event_type: type,
      event_source: source,
      event_payload: makePayload(type, i + 1),
      event_datetime: t.toISOString(),
    });
  }
  return list;
})();

// Format helpers
export const formatEventType = (t) =>
  t.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

export const formatSource = (s) =>
  ({
    web: "Web",
    mobile_app: "Mobile App",
    branch_pos: "Branch POS",
    atm_kiosk: "ATM Kiosk",
    core_banking_erp: "Core Banking (ERP)",
    call_center: "Call Center",
    partner_api: "Partner API",
  }[s] || s);

// Dashboard time series (last 14 days)
export const TIME_SERIES = (() => {
  const arr = [];
  const start = new Date("2026-02-01T00:00:00Z");
  for (let d = 0; d < 14; d++) {
    const date = new Date(start.getTime() + d * 86400000);
    arr.push({
      date: date.toISOString().slice(5, 10),
      events: Math.round(4200 + seededRand(d + 1) * 3400 + d * 90),
      approved: Math.round(680 + seededRand(d + 2) * 260),
      declined: Math.round(120 + seededRand(d + 3) * 90),
      revenue: Math.round(48000 + seededRand(d + 4) * 22000 + d * 900),
    });
  }
  return arr;
})();

export const SOURCE_BREAKDOWN = [
  { name: "Mobile App", value: 42, color: "#124273" },
  { name: "Web", value: 27, color: "#4A88C7" },
  { name: "Branch POS", value: 14, color: "#E9B44C" },
  { name: "ATM Kiosk", value: 8, color: "#4B9F86" },
  { name: "Core ERP", value: 6, color: "#E08949" },
  { name: "Other", value: 3, color: "#9AA3A8" },
];

export const FUNNEL = [
  { stage: "Card Requested", value: 12480 },
  { stage: "KYC Complete", value: 10120 },
  { stage: "Approved", value: 8340 },
  { stage: "Card Activated", value: 6210 },
  { stage: "First Transaction", value: 4180 },
];

// Workflows (Capture)
export const CAPTURE_WORKFLOWS = [
  {
    id: "wf_001",
    name: "Card Activation Nudge",
    description: "Push notification 3 days after card approval if not activated",
    status: "active",
    updated_at: "2026-02-12T09:44:00Z",
    runs_today: 218,
    success_rate: 0.94,
    nodes: [
      { id: "n1", type: "eventTrigger", position: { x: 40, y: 120 }, data: { label: "Card Approved", eventType: "credit_card_approved" } },
      { id: "n2", type: "waitAction", position: { x: 340, y: 120 }, data: { label: "Wait 3 days", value: 3, unit: "days" } },
      { id: "n3", type: "ifCondition", position: { x: 640, y: 120 }, data: { label: "Card Not Activated", expression: "{{ customer.card_activated == false }}" } },
      { id: "n4", type: "httpAction", position: { x: 980, y: 40 }, data: { label: "Send Push", method: "POST", url: "https://api.push.optikurve.io/notify" } },
      { id: "n5", type: "customScript", position: { x: 980, y: 260 }, data: { label: "Log skip", code: "log.info('activated')" } },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2", animated: true },
      { id: "e2", source: "n2", target: "n3", animated: true },
      { id: "e3", source: "n3", sourceHandle: "true", target: "n4", label: "TRUE", animated: true },
      { id: "e4", source: "n3", sourceHandle: "false", target: "n5", label: "ELSE" },
    ],
  },
  {
    id: "wf_002",
    name: "High-Value Transfer Enrichment",
    description: "Enrich large transfers with customer risk profile and log to compliance",
    status: "active",
    updated_at: "2026-02-13T14:20:00Z",
    runs_today: 47,
    success_rate: 0.99,
    nodes: [
      { id: "n1", type: "eventTrigger", position: { x: 40, y: 120 }, data: { label: "Transfer > 50k", eventType: "large_transfer_request" } },
      { id: "n2", type: "sqlEnrichment", position: { x: 340, y: 120 }, data: { label: "Get Risk Score", query: "SELECT risk_score FROM crm.customer WHERE code = :customer_code" } },
      { id: "n3", type: "customScript", position: { x: 640, y: 120 }, data: { label: "Score Logic", code: "risk = enrich.risk_score * 1.2" } },
      { id: "n4", type: "httpAction", position: { x: 940, y: 120 }, data: { label: "Notify Compliance", method: "POST", url: "https://compliance.optikurve.io/flag" } },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2", animated: true },
      { id: "e2", source: "n2", target: "n3", animated: true },
      { id: "e3", source: "n3", target: "n4", animated: true },
    ],
  },
  {
    id: "wf_003",
    name: "Abandoned Loan Recovery",
    description: "Re-engage customers who abandoned loan application at DTI step",
    status: "disabled",
    updated_at: "2026-02-10T11:05:00Z",
    runs_today: 0,
    success_rate: 0.71,
    nodes: [
      { id: "n1", type: "eventTrigger", position: { x: 40, y: 120 }, data: { label: "Loan Started", eventType: "loan_application_request" } },
      { id: "n2", type: "waitAction", position: { x: 340, y: 120 }, data: { label: "Wait 48h", value: 48, unit: "hours" } },
      { id: "n3", type: "ifCondition", position: { x: 640, y: 120 }, data: { label: "Not Approved", expression: "{{ loan.status != 'approved' }}" } },
      { id: "n4", type: "httpAction", position: { x: 980, y: 120 }, data: { label: "Email Nudge", method: "POST", url: "https://mail.optikurve.io/send" } },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2", animated: true },
      { id: "e2", source: "n2", target: "n3", animated: true },
      { id: "e3", source: "n3", sourceHandle: "true", target: "n4", label: "TRUE", animated: true },
    ],
  },
];

// Detection workflows
export const DETECTION_WORKFLOWS = [
  {
    id: "det_001",
    name: "Dormant Card Detection",
    description: "Card approved but no transaction within 30 days",
    status: "active",
    matches_today: 84,
    steps: [
      { id: "s1", op: "event_exists", event: "credit_card_approved", conditions: [{ field: "product", operator: "==", value: "Platinum Rewards" }] },
      { id: "s2", op: "event_absent", event: "credit_card_usage", within: "30 days", conditions: [] },
      { id: "s3", op: "action", action: "flag_dormant", target: "customer" },
    ],
  },
  {
    id: "det_002",
    name: "Suspicious Velocity",
    description: "5+ card transactions in different countries within 2 hours",
    status: "active",
    matches_today: 12,
    steps: [
      { id: "s1", op: "event_exists", event: "credit_card_usage", conditions: [{ field: "amount_local", operator: ">", value: 500 }] },
      { id: "s2", op: "event_count", event: "credit_card_usage", count: ">= 5", within: "2 hours" },
      { id: "s3", op: "action", action: "freeze_card", target: "card" },
    ],
  },
  {
    id: "det_003",
    name: "KYC Retry Loop",
    description: "3+ KYC verification failures without successful upload",
    status: "disabled",
    matches_today: 0,
    steps: [
      { id: "s1", op: "event_exists", event: "kyc_failed", conditions: [] },
      { id: "s2", op: "event_count", event: "kyc_failed", count: ">= 3", within: "7 days" },
      { id: "s3", op: "event_absent", event: "account_opened", within: "14 days", conditions: [] },
      { id: "s4", op: "action", action: "assign_agent", target: "customer" },
    ],
  },
];

// AI insights (fake but realistic)
export const AI_INSIGHTS = [
  {
    id: "ai_1",
    severity: "high",
    title: "Address Verification friction blocks 45% of Platinum Rewards applicants",
    body: "Customers requesting the Platinum Rewards card between 20:00 and 23:00 abandon the flow at the Address Verification step 2.3× more than the platform average. Root cause signal: OCR confidence drops below 0.6 for utility bills captured in low-light conditions.",
    metric: "45%",
    metric_label: "abandonment at step 4",
    tags: ["friction", "platinum_rewards", "kyc"],
    suggested_action: "Add a 'retake photo' hint with lighting guidance + fallback to manual address entry",
    confidence: 0.91,
  },
  {
    id: "ai_2",
    severity: "medium",
    title: "1 in 3 approved cards remain unused after 30 days",
    body: "Among approved Cashback Gold cards, 34% are never activated within 30 days. Cohort analysis shows this is concentrated in customers acquired via 'partner_referral' between Jan 10 and Jan 22. These users open the app once, view rewards, and never activate.",
    metric: "34%",
    metric_label: "dormant approved cards",
    tags: ["dormancy", "cashback_gold", "activation"],
    suggested_action: "Trigger a personalized activation offer (2x rewards for 30 days) at day 14",
    confidence: 0.87,
  },
  {
    id: "ai_3",
    severity: "high",
    title: "Emerging fraud pattern: Micro-charges preceding large transfers",
    body: "A repeating sequence of 3 sub-$5 card usage events followed by a large_transfer_request event within 20 minutes appeared in 218 accounts over the past 14 days. 84% of these accounts had prior mobile_app_login events from a new device in the same window.",
    metric: "218",
    metric_label: "accounts flagged",
    tags: ["fraud", "velocity", "device_change"],
    suggested_action: "Auto-freeze transfer & require step-up authentication when sequence detected",
    confidence: 0.94,
  },
  {
    id: "ai_4",
    severity: "low",
    title: "Loan approvals correlate with beneficiary_added within 72h",
    body: "Customers who add a new beneficiary within 72 hours after loan_approved exhibit 1.8× higher NPS and 40% lower first-payment default. This behavior signal is currently unused in your credit models.",
    metric: "1.8×",
    metric_label: "NPS uplift",
    tags: ["loan", "cross_sell"],
    suggested_action: "Feed 'post-approval beneficiary velocity' feature into next credit model retrain",
    confidence: 0.78,
  },
];

// Datasets for Configuration page
export const DATASETS = [
  { id: "ds_1", name: "Core Banking - Customer Master", type: "PostgreSQL", host: "core-db.internal.optikurve.io", status: "connected", records: "1.2M", last_sync: "2 min ago" },
  { id: "ds_2", name: "Card Transactions Stream", type: "Kafka", host: "kafka-01.internal.optikurve.io:9092", status: "connected", records: "3.8M/day", last_sync: "live" },
  { id: "ds_3", name: "CRM - Contact & Preferences", type: "Snowflake", host: "prod.snowflake.optikurve.io", status: "connected", records: "980K", last_sync: "12 min ago" },
  { id: "ds_4", name: "Marketing Attribution", type: "BigQuery", host: "bq.marketing.optikurve.io", status: "warning", records: "4.1M", last_sync: "48 min ago" },
  { id: "ds_5", name: "Legacy Loan Origination", type: "Oracle 19c", host: "legacy-lo.internal.optikurve.io", status: "disconnected", records: "215K", last_sync: "3 days ago" },
];

// Detected signals (for Detection Engine table)
export const DETECTED_SIGNALS = [
  { id: "sig_9001", detection: "Dormant Card Detection", customer: "CUST-10428", event: "credit_card_approved", detected_at: "2026-02-14T09:42:00Z", action_taken: "flag_dormant", status: "actioned" },
  { id: "sig_9002", detection: "Suspicious Velocity", customer: "CUST-10437", event: "credit_card_usage", detected_at: "2026-02-14T09:31:00Z", action_taken: "freeze_card", status: "actioned" },
  { id: "sig_9003", detection: "Dormant Card Detection", customer: "CUST-10441", event: "credit_card_approved", detected_at: "2026-02-14T08:50:00Z", action_taken: "flag_dormant", status: "actioned" },
  { id: "sig_9004", detection: "Suspicious Velocity", customer: "CUST-10432", event: "credit_card_usage", detected_at: "2026-02-14T08:20:00Z", action_taken: "freeze_card", status: "review" },
  { id: "sig_9005", detection: "Dormant Card Detection", customer: "CUST-10434", event: "credit_card_approved", detected_at: "2026-02-14T07:58:00Z", action_taken: "flag_dormant", status: "actioned" },
  { id: "sig_9006", detection: "Suspicious Velocity", customer: "CUST-10439", event: "credit_card_usage", detected_at: "2026-02-14T07:10:00Z", action_taken: "freeze_card", status: "actioned" },
  { id: "sig_9007", detection: "Dormant Card Detection", customer: "CUST-10442", event: "credit_card_approved", detected_at: "2026-02-14T06:44:00Z", action_taken: "flag_dormant", status: "actioned" },
  { id: "sig_9008", detection: "KYC Retry Loop", customer: "CUST-10430", event: "kyc_failed", detected_at: "2026-02-14T05:22:00Z", action_taken: "assign_agent", status: "review" },
  { id: "sig_9009", detection: "Suspicious Velocity", customer: "CUST-10436", event: "credit_card_usage", detected_at: "2026-02-14T04:14:00Z", action_taken: "freeze_card", status: "actioned" },
  { id: "sig_9010", detection: "Dormant Card Detection", customer: "CUST-10429", event: "credit_card_approved", detected_at: "2026-02-13T23:41:00Z", action_taken: "flag_dormant", status: "actioned" },
  { id: "sig_9011", detection: "Dormant Card Detection", customer: "CUST-10440", event: "credit_card_approved", detected_at: "2026-02-13T22:15:00Z", action_taken: "flag_dormant", status: "dismissed" },
  { id: "sig_9012", detection: "Suspicious Velocity", customer: "CUST-10438", event: "credit_card_usage", detected_at: "2026-02-13T21:03:00Z", action_taken: "freeze_card", status: "actioned" },
];

// Events lifecycle -- captured -> qualified -> activated -> measured
export const LIFECYCLE_FUNNEL = [
  { stage: "Captured", value: 12480, sub: "raw ingestion" },
  { stage: "Qualified", value: 8940, sub: "matched a workflow" },
  { stage: "Activated", value: 6210, sub: "action fired" },
  { stage: "Measured", value: 4180, sub: "outcome recorded" },
];

export const LIFECYCLE_BY_TYPE = [
  { event: "credit_card_request", captured: 3820, qualified: 3120, activated: 2440, converted: 1860, unit_value: 480, total_value: 892800, avg_time_ms: 42 },
  { event: "credit_card_usage", captured: 4110, qualified: 3980, activated: 3980, converted: 3820, unit_value: 12, total_value: 45840, avg_time_ms: 28 },
  { event: "credit_card_request_abandoned", captured: 1240, qualified: 1240, activated: 940, converted: 540, unit_value: 320, total_value: 172800, avg_time_ms: 48 },
  { event: "large_transfer_request", captured: 218, qualified: 218, activated: 218, converted: 214, unit_value: 320, total_value: 68480, avg_time_ms: 51 },
  { event: "loan_application_request", captured: 640, qualified: 480, activated: 340, converted: 210, unit_value: 2400, total_value: 504000, avg_time_ms: 88 },
  { event: "loan_application_abandoned", captured: 320, qualified: 320, activated: 240, converted: 118, unit_value: 1800, total_value: 212400, avg_time_ms: 74 },
  { event: "account_opening_request", captured: 1240, qualified: 1120, activated: 940, converted: 720, unit_value: 260, total_value: 187200, avg_time_ms: 44 },
  { event: "mobile_app_login", captured: 1862, qualified: 432, activated: 218, converted: 88, unit_value: 22, total_value: 1936, avg_time_ms: 18 },
];

// Measure page -- impact
export const MEASURE_SUMMARY = {
  revenue_generated: 2140000,
  cost_prevented: 418000,
  churn_saved: 92000,
  hours_saved: 1240,
  customers_impacted: 8420,
  roi_multiple: 8.4,
};

export const MEASURE_BY_WORKFLOW = [
  { workflow: "Card Activation Nudge", type: "capture", runs: 6540, actioned: 6210, converted: 2180, revenue: 892000, cost_saved: 0, status: "active" },
  { workflow: "High-Value Transfer Enrichment", type: "capture", runs: 1420, actioned: 1418, converted: 214, revenue: 0, cost_saved: 68000, status: "active" },
  { workflow: "Abandoned Loan Recovery", type: "capture", runs: 0, actioned: 0, converted: 0, revenue: 0, cost_saved: 0, status: "disabled" },
  { workflow: "Dormant Card Detection", type: "detection", runs: 2640, actioned: 2438, converted: 892, revenue: 428000, cost_saved: 0, status: "active" },
  { workflow: "Suspicious Velocity", type: "detection", runs: 340, actioned: 322, converted: 318, revenue: 0, cost_saved: 350000, status: "active" },
  { workflow: "KYC Retry Loop", type: "detection", runs: 0, actioned: 0, converted: 0, revenue: 0, cost_saved: 0, status: "disabled" },
  { workflow: "Card Adoption Friction Radar", type: "ai", runs: 8420, actioned: 218, converted: 84, revenue: 820000, cost_saved: 0, status: "active" },
];

export const MEASURE_TIMELINE = (() => {
  const arr = [];
  for (let d = 0; d < 14; d++) {
    const seed = d + 1;
    arr.push({
      date: `02-${String(d + 1).padStart(2, "0")}`,
      revenue: Math.round(120000 + Math.sin(seed) * 40000 + d * 4000),
      cost_saved: Math.round(24000 + Math.cos(seed * 1.4) * 8000 + d * 500),
    });
  }
  return arr;
})();

// AI Detection use cases with history (who did what -- back-office actions)
export const AI_USECASES = [
  {
    id: "aic_1",
    name: "Card Adoption Friction Radar",
    scope: "credit_cards",
    prompt: "Detect friction that stops someone who ordered a credit card from using it within a month",
    status: "listening",
    deployed_by: "backend.detection@optikurve",
    deployed_at: "2026-02-09T14:20:00Z",
    signals_captured: 218,
    last_signal_at: "12 minutes ago",
    linked_insights: [AI_INSIGHTS[0], AI_INSIGHTS[1]],
    history: [
      { at: "2026-02-14T09:12:00Z", who: "detection-runtime", action: "signal captured", detail: "45% Platinum abandonment at Address Verification step" },
      { at: "2026-02-13T18:44:00Z", who: "sarah.mitchell (analyst)", action: "reviewed insight", detail: "Marked address-verification friction as high priority" },
      { at: "2026-02-12T11:20:00Z", who: "detection-runtime", action: "threshold recalibrated", detail: "OCR confidence floor lowered 0.65 -> 0.60" },
      { at: "2026-02-11T09:05:00Z", who: "michael.thompson (ops)", action: "linked to workflow", detail: "Wired to 'Card Activation Nudge' workflow" },
      { at: "2026-02-09T14:20:00Z", who: "backend.detection@optikurve", action: "engine deployed", detail: "Initial deployment with 14-day backfill" },
    ],
  },
  {
    id: "aic_2",
    name: "Silent Churn Signal",
    scope: "all",
    prompt: "Find silent churn signals in mobile app usage before customers close their account",
    status: "listening",
    deployed_by: "backend.detection@optikurve",
    deployed_at: "2026-02-06T10:15:00Z",
    signals_captured: 142,
    last_signal_at: "24 minutes ago",
    linked_insights: [AI_INSIGHTS[2], AI_INSIGHTS[3]],
    history: [
      { at: "2026-02-14T08:48:00Z", who: "detection-runtime", action: "signal captured", detail: "218 accounts flagged with micro-charge -> large transfer pattern" },
      { at: "2026-02-13T14:20:00Z", who: "jennifer.anderson (risk lead)", action: "approved model update", detail: "Promoted device-change feature to primary signal" },
      { at: "2026-02-10T16:30:00Z", who: "detection-runtime", action: "feature enriched", detail: "Added 'days since last login' as covariate" },
      { at: "2026-02-08T11:05:00Z", who: "david.wilson (product)", action: "reviewed insight", detail: "Cross-sell opportunity flagged for loan cohort" },
      { at: "2026-02-06T10:15:00Z", who: "backend.detection@optikurve", action: "engine deployed", detail: "Initial deployment with 30-day backfill" },
    ],
  },
];

// Post-action measurement -- what happened after each capture workflow fired an action
export const MEASURE_CAMPAIGNS = [
  {
    id: "camp_1",
    name: "Card Activation Reminder",
    workflow: "Card Activation Nudge",
    channel: "push",
    trigger_event: "credit_card_approved",
    desired_outcome: "credit_card_activation",
    sent: 8420, delivered: 8180, read: 5720, acted: 2140, converted: 1780,
    revenue: 892000, avg_time_to_convert_days: 4.2,
  },
  {
    id: "camp_2",
    name: "Abandoned Request Recovery",
    workflow: "Card Request Recovery",
    channel: "email",
    trigger_event: "credit_card_request_abandoned",
    desired_outcome: "credit_card_request (completed)",
    sent: 1240, delivered: 1210, read: 720, acted: 320, converted: 210,
    revenue: 168000, avg_time_to_convert_days: 2.1,
  },
  {
    id: "camp_3",
    name: "Loan Follow-up",
    workflow: "Abandoned Loan Recovery",
    channel: "email",
    trigger_event: "loan_application_abandoned",
    desired_outcome: "loan_application_request (resumed)",
    sent: 640, delivered: 620, read: 380, acted: 168, converted: 118,
    revenue: 283000, avg_time_to_convert_days: 5.4,
  },
  {
    id: "camp_4",
    name: "First-time Card Usage Bonus",
    workflow: "Card Activation Nudge",
    channel: "sms",
    trigger_event: "credit_card_activation",
    desired_outcome: "credit_card_usage",
    sent: 3120, delivered: 3080, read: 2410, acted: 1240, converted: 942,
    revenue: 428000, avg_time_to_convert_days: 3.1,
  },
  {
    id: "camp_5",
    name: "Beneficiary Cross-sell",
    workflow: "Beneficiary Follow-up",
    channel: "in-app",
    trigger_event: "beneficiary_added",
    desired_outcome: "large_transfer_request",
    sent: 480, delivered: 480, read: 342, acted: 128, converted: 96,
    revenue: 118000, avg_time_to_convert_days: 6.8,
  },
];

// Time series for read/convert rates (14 days)
export const MEASURE_RATES_TIMELINE = (() => {
  const arr = [];
  for (let d = 0; d < 14; d++) {
    const s = d + 1;
    arr.push({
      date: `02-${String(d + 1).padStart(2, "0")}`,
      read_rate: Math.round((0.62 + Math.sin(s) * 0.05 + d * 0.003) * 100),
      convert_rate: Math.round((0.18 + Math.cos(s * 1.3) * 0.03 + d * 0.002) * 100),
    });
  }
  return arr;
})();
